import { GroupWidgetPort, Position, WidgetGroupState, WidgetType } from '@kemu-io/kemu-core/types';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { createWidgetPortIdentifier, decodeWidgetPortIdentifier, encodePortName, OUTER_OUTPUT_PREFIX, OUTER_INPUT_PREFIX, INNER_OUTPUT_PREFIX } from '@kemu-io/kemu-core/common/utils';
import { DataType, WidgetPort } from '@kemu-io/hs-types';
import kemuCore from '@kemu-io/kemu-core';
import { LogicMapperState, SelectedWidgetInfo } from '../logicMapperSlice';
import * as RecipeActions from '@src/app/recipe/utils';
import { safeJsonClone } from '@common/utils';
import { RootState } from '@src/app/store';
import { StatelessRecipeWidget, StatelessWidgetsMap } from '@src/types/core_t';

type GroupWidgetsProps = {
  recipeId: string,
  thingId: string,
  widgets: SelectedWidgetInfo[],
  parentGroupId?: string,
}

// Constants for positioning and spacing
const INPUT_PORT_OFFSET_X = 200; // Distance to the left of widgets for input ports
const OUTPUT_PORT_OFFSET_X = 150; // Distance to the right of widgets for output ports
const PORT_VERTICAL_OFFSET = 50; // Vertical offset from top of widget area
const PORT_SPACING = 60; // Vertical spacing between ports
const DEFAULT_POSITION = 100; // Default fallback position
const DEFAULT_OUTPUT_PORT_X = 500; // Default X position for output ports when bounding box is invalid
const CANVAS_PADDING = 100; // Padding for canvas viewport positioning
const DEFAULT_WIDGET_WIDTH = 100;
const DEFAULT_WIDGET_HEIGHT = 54;

/**
 * Normalizes a port type to always be a DataType array.
 * WidgetPort.type can be DataType | DataType[] | undefined | null,
 * but GroupWidgetPort.type must always be DataType[].
 * 
 * @param type - The port type from WidgetPort (can be single value, array, or null/undefined)
 * @returns A valid DataType array, defaulting to [DataType.Anything] if type is missing
 */
const normalizePortType = (type: DataType | DataType[] | undefined | null): DataType[] => {
  if (Array.isArray(type)) {
    return type;
  }
  if (type != null) {
    return [type];
  }
  return [DataType.Anything];
};

/**
 * Gets output ports for a widget.
 * For all widgets (including hub services and play widgets), we await kemuCore.getWidgetOutputs()
 * which works for both synchronous (regular widgets) and asynchronous (hub services) cases.
 */
const getWidgetOutputPorts = async (
  widgetId: string,
  thingId: string,
  recipeId: string
): Promise<WidgetPort[]> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ports = await (kemuCore as any).getWidgetOutputs({
    widgetId,
    thingId,
    recipeId,
  });

  return Array.isArray(ports) ? ports : [];
};

/**
 * Gets input ports for a widget.
 * For all widgets (including hub services), we await kemuCore.getWidgetInputs()
 * which works for both synchronous (regular widgets) and asynchronous (hub services) cases.
 * Play widgets have no inputs, so the API will return an empty array.
 */
const getWidgetInputPorts = async (
  widgetId: string,
  thingId: string,
  recipeId: string
): Promise<WidgetPort[]> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ports = await (kemuCore as any).getWidgetInputs({
    widgetId,
    thingId,
    recipeId,
  });

  return Array.isArray(ports) ? ports : [];
};

/**
 * Checks if a port name should be processed when grouping.
 * For widget groups, only outer ports should be processed (inner ports are internal).
 */
const shouldProcessPort = (portName: string, isInput: boolean, widgetType: WidgetType): boolean => {
  if (widgetType !== WidgetType.widgetGroup) {
    return true;
  }
  const prefix = isInput ? OUTER_INPUT_PREFIX : OUTER_OUTPUT_PREFIX;
  return portName.startsWith(prefix);
};

/**
 * Gets the display name for a widget.
 * For widget groups, uses the widget's state.name if available.
 * For regular widgets, falls back to the widget type.
 * 
 * @param widget - The widget to get the display name for
 * @returns The widget's display name
 */
const getWidgetDisplayName = (widget: StatelessRecipeWidget | undefined): string => {
  if (!widget) {
    return 'Unknown';
  }
  // For widget groups, check if state has a name property
  if (widget.type === WidgetType.widgetGroup && widget.state && typeof widget.state === 'object' && 'name' in widget.state) {
    const widgetState = widget.state as { name?: string };
    if (widgetState.name) {
      return widgetState.name;
    }
  }
  // Fallback to widget type
  return widget.type;
};

/**
 * Generates a unique port label, adding widget name prefix and numeric suffix when needed.
 * - If the base port label is unique, use it as-is
 * - If duplicate, add widget name prefix with numeric suffix starting from 1 (e.g., "COUNTER - output 1", "COUNTER - output 2")
 * 
 * @param basePortLabel - The base port label (port.label || port.name)
 * @param widgetName - The widget display name to use as prefix
 * @param existingLabels - Array of existing port labels to check against
 * @returns A unique port label
 */
const generateUniquePortLabel = (
  basePortLabel: string,
  widgetName: string,
  existingLabels: string[]
): string => {
  // If base label is unique, use it as-is
  if (!existingLabels.includes(basePortLabel)) {
    return basePortLabel;
  }

  // Base label is duplicate, create prefixed label with numeric suffix starting from 1
  const prefixedBase = `${widgetName} - ${basePortLabel}`;
  let suffix = 1;
  let candidateLabel = `${prefixedBase} ${suffix}`;
  while (existingLabels.includes(candidateLabel)) {
    suffix++;
    candidateLabel = `${prefixedBase} ${suffix}`;
  }

  return candidateLabel;
};

/**
 * Creates a port position based on base coordinates and index.
 */
const createPortPosition = (baseX: number, baseY: number, index: number): Position => ({
  x: baseX,
  y: baseY + (index * PORT_SPACING),
});

/**
 * Creates a new group port from a widget port.
 * This helper function reduces duplication between input and output port creation.
 */
type CreateGroupPortParams = {
  sourcePort: WidgetPort;
  portName: string;
  portLabel: string;
  index: number;
  portType: DataType | DataType[] | undefined | null;
  baseX: number;
  baseY: number;
};

const createGroupPort = (params: CreateGroupPortParams): GroupWidgetPort => {
  const { sourcePort, portName, portLabel, index, portType, baseX, baseY } = params;
  return {
    ...sourcePort,
    type: normalizePortType(portType),
    name: portName,
    label: portLabel,
    index,
    position: createPortPosition(baseX, baseY, index),
  };
};

/**
 * Creates a new group widget and assigns the selected widgets to it.
 */
const groupSelectedWidgetsAction = createAsyncThunk(
  '/LogicMapper/groupSelectedWidgets', async (
  payload: GroupWidgetsProps,
  thunkApi
) : Promise<StatelessWidgetsMap> => {
    const state = thunkApi.getState() as RootState;
    const allWidgets = state.logicMapper.gates;
    const thingWidgets = RecipeActions.getWidgetsInThing(payload.recipeId, payload.thingId);

    // Get the actual widget info from redux
    const selectedWidgets = payload.widgets.map(info => allWidgets[info.widgetId]).filter(Boolean);
    const selectedWidgetIds = new Set(selectedWidgets.map(w => w.id));

    // 1. Calculate bounding box of selected widgets to position virtual ports correctly
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    selectedWidgets.forEach(widget => {
        if (widget.canvas) {
            const x = widget.canvas.position.x;
            const y = widget.canvas.position.y;

            // Try to get actual widget dimensions from DOM, fallback to defaults
            const domEl = RecipeActions.getEntityDomEl(payload.recipeId, payload.thingId, widget.id);
            const width = domEl ? domEl.getBoundingClientRect().width : DEFAULT_WIDGET_WIDTH;
            const height = domEl ? domEl.getBoundingClientRect().height : DEFAULT_WIDGET_HEIGHT;

            // Calculate widget bounds (position is top-left)
            const widgetRight = x + width;
            const widgetBottom = y + height;

            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, widgetRight);
            maxY = Math.max(maxY, widgetBottom);
        }
    });

    // Calculate centroid for group widget position
    // Fallback to default if no valid bounding box
    const centroid: Position = {
        x: isFinite(minX) && isFinite(maxX) ? (minX + maxX) / 2 : DEFAULT_POSITION,
        y: isFinite(minY) && isFinite(maxY) ? (minY + maxY) / 2 : DEFAULT_POSITION,
    };

    // Calculate initial virtual port positions based on widget bounding box
    // Input ports (left side) - position near the top of the widget area
    const inputPortBaseX = isFinite(minX) ? minX - INPUT_PORT_OFFSET_X : DEFAULT_POSITION;
    const inputPortBaseY = isFinite(minY) ? minY + PORT_VERTICAL_OFFSET : DEFAULT_POSITION;
    // Output ports (right side) - position further to the right from widgets
    const outputPortBaseX = isFinite(maxX) ? maxX + OUTPUT_PORT_OFFSET_X : DEFAULT_OUTPUT_PORT_X;
    const outputPortBaseY = isFinite(minY) ? minY + PORT_VERTICAL_OFFSET : DEFAULT_POSITION;

    // 2. Create group widget
    // Calculate initial canvas position to show the grouped widgets and virtual ports
    // Position the viewport to show the leftmost virtual ports and top of widgets
    const initialCanvasX = isFinite(inputPortBaseX) ? inputPortBaseX - CANVAS_PADDING : 0;
    const initialCanvasY = isFinite(minY) ? minY - CANVAS_PADDING : 0;

    const defaultState: Partial<WidgetGroupState> = {
      name: 'Group',
      description: '',
      inputs: [],
      outputs: [],
      settings: [],
      protocolVersion: '2',
      canvasPosition: {
        x: initialCanvasX,
        y: initialCanvasY,
      },
      canvasZoom: 1,
    };

    const newGroupWidget = RecipeActions.addCustomWidgetToThing(
      payload.recipeId,
      payload.thingId,
      centroid,
      defaultState as WidgetGroupState,
      payload.parentGroupId
    );

    // 3. Find connections from external widgets to the ones being grouped
    const groupInputs: GroupWidgetPort[] = [];
    const createdGroupPorts = new Set<string>();

    for (const selectedWidget of selectedWidgets) {
      // Returns a list of widgets that are parents of the given widget
      const parents = RecipeActions.getWidgetParents({
        widgetId: selectedWidget.id,
        blockId: payload.thingId,
        recipeId: payload.recipeId
      });

      for (const parent of parents) {
        if (selectedWidgetIds.has(parent.parentId)) {
          // Parent is also in the selection, so we ignore this connection as it will be internal to the group.
          continue;
        }

        // Now we have an external parent. We need to find which port of `selectedWidget` it connects to.
        const originalTargetPort = decodeWidgetPortIdentifier(parent.targetPort);
        const originalTargetPortName = originalTargetPort.portName;

        // For widget groups, only process outer ports (inner ports are internal to the group)
        if (!shouldProcessPort(originalTargetPortName, true, selectedWidget.type)) {
          continue;
        }

        // Get the parent widget's output port type - this is what's actually being sent
        const originalSourcePort = decodeWidgetPortIdentifier(parent.sourcePort);
        const originalSourcePortName = originalSourcePort.portName;
        const parentWidget = allWidgets[parent.parentId];

        // For widget groups as parents, filter ports based on whether they're from the containing group
        if (parentWidget && parentWidget.type === WidgetType.widgetGroup) {
          const isContainingGroup = payload.parentGroupId === parent.parentId;
          const isInnerOutputPort = originalSourcePortName.startsWith(INNER_OUTPUT_PREFIX);

          // Skip inner output ports from non-containing groups (they're internal to those groups)
          if (!isContainingGroup && isInnerOutputPort) {
            continue;
          }

          // Skip non-outer output ports from non-containing groups
          if (!isContainingGroup && !shouldProcessPort(originalSourcePortName, false, parentWidget.type)) {
            continue;
          }

          // If isContainingGroup, allow both outer and inner output ports
          // (inner output ports of the containing group are external to widgets being grouped)
        }

        // Get parent widget's output ports to determine the actual type being sent
        const parentOutputPorts = parentWidget
          ? await getWidgetOutputPorts(
              parent.parentId,
              payload.thingId,
              payload.recipeId
            )
          : [];

        const matchingParentOutput = parentOutputPorts.find(p => p.name === originalSourcePortName);

        // Get child widget's input ports to find the matching input
        const childInputPorts = await getWidgetInputPorts(
          selectedWidget.id,
          payload.thingId,
          payload.recipeId
        );

        const matchingInput = childInputPorts.find((input: WidgetPort) => input.name === originalTargetPortName);

        if (matchingInput) {
          const portId = `${originalTargetPort.widgetId}-${matchingInput.name}`;
          if (createdGroupPorts.has(portId)) { continue; }

          // Get the base port label (what the user sees)
          const basePortLabel = matchingInput.label || matchingInput.name;

          // Generate unique port label with widget name prefix and numeric suffix if needed
          // Use the child widget name (target) for input ports, not the parent widget name (source)
          const existingLabels = groupInputs.map(port => port.label).filter((label): label is string => label !== undefined);
          const widgetName = getWidgetDisplayName(selectedWidget);
          const portLabel = generateUniquePortLabel(basePortLabel, widgetName, existingLabels);

          const existingPorts = groupInputs.map(port => port.name);
          const newPortName = RecipeActions.getRandomPortName(portLabel, existingPorts);

          // Use the parent's output port type instead of the child's input port type
          // This ensures the group input port matches what's actually being sent
          const portTypeToUse = matchingParentOutput?.type ?? matchingInput.type;

          const newPort = createGroupPort({
            sourcePort: matchingInput,
            portName: newPortName,
            portLabel,
            index: groupInputs.length,
            portType: portTypeToUse,
            baseX: inputPortBaseX,
            baseY: inputPortBaseY,
          });

          groupInputs.push(newPort);
          createdGroupPorts.add(portId);

          // Update the parent's reference
          const childConnection = thingWidgets[parent.parentId].children?.find((child) => {
            return child.targetPort === parent.targetPort;
          });

          if (childConnection) {
            const targetName = encodePortName('outer', 'input', newPort.name);
            const prevTargetPort = childConnection.targetPort;
            const prevTargetChild = childConnection.childId;

            childConnection.targetPort = createWidgetPortIdentifier(newGroupWidget.id, 'input', targetName);
            childConnection.childId = newGroupWidget.id;

            const sourceName = encodePortName('inner', 'input', newPort.name);
            // link the inner port to previous child
            newGroupWidget.children.push({
              childId: prevTargetChild,
              targetPort: prevTargetPort,
              sourcePort: createWidgetPortIdentifier(newGroupWidget.id, 'innerOutput', sourceName),
            });
          }
        }
      }
    }

    // 4. Update the newly added group's state to include the new inputs
    newGroupWidget.state = {
      ...newGroupWidget.state,
      inputs: groupInputs,
    };

    // 5. Find connections from the widgets being grouped to external widgets
    const groupOutputs: GroupWidgetPort[] = [];
    const outputPortMapping = new Map<string, string>(); // Map<original_sourcePortIdentifier, new_group_port_name>

    for (const selectedWidget of selectedWidgets) {
        const widgetWithChildren = thingWidgets[selectedWidget.id];
        if (!widgetWithChildren?.children) {
            continue;
        }

        const childrenCopy = [...widgetWithChildren.children];
        for (const childConnection of childrenCopy) {
            if (selectedWidgetIds.has(childConnection.childId)) {
                continue;
            }

            const originalSourcePortIdentifier = childConnection.sourcePort;
            const originalSourcePort = decodeWidgetPortIdentifier(originalSourcePortIdentifier);
            const originalSourcePortName = originalSourcePort.portName;

            // For widget groups, only process outer ports (inner ports are internal to the group)
            if (!shouldProcessPort(originalSourcePortName, false, selectedWidget.type)) {
              continue;
            }

            // Get output ports for the selected widget
            const parentOutputPorts = await getWidgetOutputPorts(
              selectedWidget.id,
              payload.thingId,
              payload.recipeId
            );

            const matchingOutput = parentOutputPorts.find(output => output.name === originalSourcePortName);

            if (matchingOutput) {
                let newPortName: string;
                if (outputPortMapping.has(originalSourcePortIdentifier)) {
                    newPortName = outputPortMapping.get(originalSourcePortIdentifier)!;
                } else {
                    // Get the base port label (what the user sees)
                    const basePortLabel = matchingOutput.label || matchingOutput.name;

                    // Generate unique port label with widget name prefix and numeric suffix if needed
                    const existingLabels = groupOutputs.map(port => port.label).filter((label): label is string => label !== undefined);
                    const widgetName = getWidgetDisplayName(selectedWidget);
                    const portLabel = generateUniquePortLabel(basePortLabel, widgetName, existingLabels);

                    const existingPorts = groupOutputs.map(port => port.name);
                    newPortName = RecipeActions.getRandomPortName(portLabel, existingPorts);
                    outputPortMapping.set(originalSourcePortIdentifier, newPortName);

                    const newPort = createGroupPort({
                      sourcePort: matchingOutput,
                      portName: newPortName,
                      portLabel,
                      index: groupOutputs.length,
                      portType: matchingOutput.type,
                      baseX: outputPortBaseX,
                      baseY: outputPortBaseY,
                    });
                    groupOutputs.push(newPort);
                }

                const originalChildId = childConnection.childId;
                const originalTargetPort = childConnection.targetPort;
                const widgetToUpdate = thingWidgets[selectedWidget.id];
                const childConnectionIndex = widgetToUpdate.children.findIndex(c =>
                    c.childId === originalChildId && c.targetPort === originalTargetPort
                );

                if (childConnectionIndex > -1) {
                    const innerGroupName = encodePortName('inner', 'output', newPortName);
                    const outerGroupName = encodePortName('outer', 'output', newPortName);

                    // Update selected widget's child connection to point to the group's inner-output port
                    widgetToUpdate.children[childConnectionIndex] = {
                        ...widgetToUpdate.children[childConnectionIndex],
                        childId: newGroupWidget.id,
                        targetPort: createWidgetPortIdentifier(newGroupWidget.id, 'innerInput', innerGroupName),
                    };

                    // Add a new connection from the group's outer-output to the original external child
                    newGroupWidget.children.push({
                        childId: originalChildId,
                        targetPort: originalTargetPort,
                        sourcePort: createWidgetPortIdentifier(newGroupWidget.id, 'output', outerGroupName),
                    });
                }
            }
        }
    }

    // 6. Update the newly added group's state to include the new outputs
    newGroupWidget.state = {
      ...newGroupWidget.state,
      outputs: groupOutputs,
    };

    // 7. Update selected widgets with groupId
    const updatedThingWidgets = RecipeActions.getWidgetsInThing(payload.recipeId, payload.thingId);
    for (const selected of selectedWidgets) {
      const widgetToUpdate = updatedThingWidgets[selected.id];
      if (widgetToUpdate) {
        widgetToUpdate.groupId = newGroupWidget.id;
      }
    }

    // 8. Create a stateless dictionary of the widgets to store in redux
    const stateLessWidgets: StatelessWidgetsMap = {};
    Object.values(updatedThingWidgets).forEach(widget => {
        const { state: _state, ...stateLess } = widget;
        stateLessWidgets[widget.id] = stateLess as StatelessRecipeWidget;
    });

    return safeJsonClone(stateLessWidgets);
});

const groupWidgetsReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
    builder.addCase(groupSelectedWidgetsAction.fulfilled, (state, action) => {
        state.gates = action.payload;
        // Clear selection after grouping widgets
        state.selectedWidgets = [];
    });

    builder.addCase(groupSelectedWidgetsAction.rejected, (_state, action) => {
        console.error('Failed to group widgets: ', action.error);
    });
});

export default {
  action: groupSelectedWidgetsAction,
  reducer: groupWidgetsReducer,
};
